import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CardSensitiveDetailsRendererService } from '../../../../lib/services/card-management-service/card-sensitive-details-renderer.service';
import { CardManagementService } from '../../../../lib/services/card-management-service/card-management-service';
import { OverlayConfirmationService } from '../../../../lib/services/overlay-confirmation-service/overlay-confirmation.service';
import { CardState } from '../../../../lib/state/card.state';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  CardStatusType,
  CardType,
  ConfirmationClaimType,
  PaymentProvider,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { Subject, firstValueFrom, takeUntil, tap } from 'rxjs';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-change-pin',
  templateUrl: './change-pin.component.html',
  styleUrls: ['./change-pin.component.scss'],
})
export class ChangePinComponent implements OnInit, OnDestroy {
  @AutoDestroy destroy$ = new Subject<void>();
  @Input() isSeparateZone: boolean;
  @Input() isWorkflow: boolean;
  @Input() borderStyle: baseCssConfigStyle;
  @Input() textContentStyle: baseCssConfigStyle;
  @Input() contentStyle: baseCssConfigStyle;
  @Output() openPopupEvent = new EventEmitter();

  isWidgetEnabled: boolean;
  showAirwallexIframe = false;
  iframeHostId = 'awx-change-pin-host';
  iframeError?: string;
  iframeMessage?: string;

  constructor(
    private store: Store,
    private cardManagementService: CardManagementService,
    private cardSensitiveDetailsRendererService: CardSensitiveDetailsRendererService,
    private overlayConfirmationService: OverlayConfirmationService,
  ) {}

  ngOnInit(): void {
    this.store
      .select(CardState.getCurrentCardDetail)
      .pipe(
        takeUntil(this.destroy$),
        tap((cardResponse) => {
          if (cardResponse) {
            const isAirwallex = this.isAirwallexProvider();
            this.isWidgetEnabled =
              cardResponse.status === CardStatusType.ACTIVE &&
              (isAirwallex || cardResponse?.type?.toLowerCase() !== CardType.VIRTUAL);
            const suffix = (cardResponse.id ?? 'card').replace(/[^a-zA-Z0-9_-]/g, '');
            this.iframeHostId = `awx-change-pin-${suffix}`;
          }
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    void this.cardSensitiveDetailsRendererService.teardown();
  }

  isAirwallexProvider(): boolean {
    return (
      this.store.selectSnapshot((state: { projectSettings?: { provider?: string } }) => state.projectSettings?.provider) ===
      PaymentProvider.AIRWALLEX
    );
  }

  async openChangePinPopup() {
    if (!this.isWidgetEnabled) {
      return;
    }
    if (!this.isAirwallexProvider()) {
      this.openPopupEvent.emit(true);
      return;
    }

    this.iframeError = undefined;
    this.iframeMessage = undefined;
    this.showAirwallexIframe = true;
    const cardId = this.store.selectSnapshot(CardState.getCurrentCardId);
    try {
      await this.overlayConfirmationService.confirm(ConfirmationClaimType.GET_PIN, 'cards.change-pin');
      const token = await firstValueFrom(this.cardManagementService.getSecureToken({ cardId }));
      await this.cardSensitiveDetailsRendererService.renderAirwallexIframe(
        {
          provider: PaymentProvider.AIRWALLEX,
          mode: 'provider-sdk',
          providerSession: {
            sdk: 'airwallex',
            accessToken: token.token,
            expiresAt: token.expiresAt,
            iframeOrigin: token.iframeOrigin,
          },
          providerData: { cardId: token.providerCardId ?? cardId },
        },
        'change-pin',
        this.iframeHostId,
        {
          onSuccess: () => {
            this.iframeMessage = 'PIN updated.';
          },
          onCancel: () => {
            this.showAirwallexIframe = false;
          },
          onFailure: (error) => {
            this.iframeError = error instanceof Error ? error.message : String(error);
          },
        },
      );
    } catch (error) {
      this.iframeError = error instanceof Error ? error.message : 'Unable to open the change PIN form.';
    }
  }
}
